import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Droplet, 
  Activity, 
  Brain, 
  Smile, 
  CheckCircle2, 
  Circle,
  Sparkles,
  Award,
  TrendingUp,
  Heart,
  Star,
  Zap,
  Sun
} from 'lucide-react';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import healthApi from '@/api/healthApi';

/**
 * Daily Health Loop - Habit tracking with real backend integration
 * Features: Streaks, badges, personalized insights from real data
 */

// Badge definitions
const BADGES = {
  starter: { name: 'Getting Started', icon: Star, color: 'text-yellow-500', threshold: 1 },
  consistent: { name: 'Consistent', icon: Award, color: 'text-blue-500', threshold: 3 },
  dedicated: { name: 'Dedicated', icon: Sparkles, color: 'text-purple-500', threshold: 7 },
  champion: { name: 'Health Champion', icon: Zap, color: 'text-orange-500', threshold: 14 },
  legend: { name: 'Wellness Legend', icon: Sun, color: 'text-pink-500', threshold: 30 }
};

// Encouraging messages
const ENCOURAGEMENT = [
  "You're building healthy habits 🌱",
  "Great job checking in today! ✨",
  "Every small step matters 💪",
  "You're taking care of yourself 💙",
  "Consistency is your superpower 🌟",
  "You showed up today! 🎉",
  "Your future self will thank you 🙏",
  "Progress, not perfection 🌈"
];

const DailyHealthLoop = () => {
  const navigate = useNavigate();
  const [showConfetti, setShowConfetti] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Daily checklist state
  const [checklist, setChecklist] = useState({
    water: false,
    movement: false,
    stress: false,
    mood: false
  });

  // Streaks, badges, and insights from backend
  const [streaks, setStreaks] = useState({
    daily_streak: 0,
    hydration_streak: 0,
    movement_streak: 0
  });
  const [badges, setBadges] = useState({
    earned_badges: [],
    total_points: 0
  });
  const [insight, setInsight] = useState('');
  const [encouragementMsg, setEncouragementMsg] = useState('');

  // Load today's habits, streaks, and badges
  const loadDailyData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch today's habits
      const habitsResponse = await healthApi.getTodayHabits();
      if (habitsResponse.data.data) {
        setChecklist(habitsResponse.data.data.habits || {
          water: false,
          movement: false,
          stress: false,
          mood: false
        });
      }

      // Fetch streaks
      const streaksResponse = await healthApi.getUserStreaks();
      setStreaks(streaksResponse.data.data);

      // Fetch badges
      const badgesResponse = await healthApi.getUserBadges();
      setBadges(badgesResponse.data.data);

    } catch (error) {
      // Remove console.error in production
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading daily data:', error);
      }
      toast.error('Failed to load your data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    loadDailyData();
    setEncouragementMsg(ENCOURAGEMENT[Math.floor(Math.random() * ENCOURAGEMENT.length)]);
  }, [loadDailyData]);

  // Handle checklist toggle
  const handleToggle = async (item) => {
    const newChecklist = { ...checklist, [item]: !checklist[item] };
    const wasChecked = checklist[item];
    
    // Optimistic update
    setChecklist(newChecklist);

    if (!wasChecked) {
      // Item was just checked - show immediate feedback
      const messages = {
        water: "💧 Great hydration!",
        movement: "🏃 Way to move!",
        stress: "🧘 Stress check done!",
        mood: "😊 Mood logged!"
      };
      toast.success(messages[item]);
    }

    try {
      // Save to backend
      const response = await healthApi.logDailyHabits(newChecklist);
      
      if (response.data.success) {
        const { streaks: newStreaks, badges: newBadges, insight: newInsight } = response.data.data;
        
        // Update state with backend response
        setStreaks(newStreaks);
        setInsight(newInsight);

        // Check for new badges
        const previousBadges = badges.earned_badges || [];
        const currentBadges = newBadges.earned_badges || [];
        const newlyEarnedBadges = newBadges.new_badges || [];

        if (newlyEarnedBadges.length > 0) {
          newlyEarnedBadges.forEach(badgeKey => {
            const badge = BADGES[badgeKey];
            if (badge) {
              toast.success(`🏆 New badge unlocked: ${badge.name}!`, { duration: 5000 });
            }
          });
        }

        setBadges(newBadges);

        // Check if all items completed
        if (allCompleted(newChecklist) && !wasAllCompletedBefore(checklist)) {
          handleAllCompleted(newInsight);
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error saving habit:', error);
      }
      // Revert optimistic update on error
      setChecklist(checklist);
      toast.error('Failed to save. Please try again.');
    }
  };

  // Check if all items completed
  const allCompleted = (checklistData) => {
    return Object.values(checklistData).every(val => val === true);
  };

  // Check if was all completed before this toggle
  const wasAllCompletedBefore = (previousChecklist) => {
    return Object.values(previousChecklist).every(val => val === true);
  };

  // Handle all items completed
  const handleAllCompleted = (generatedInsight) => {
    // Show celebration
    setShowConfetti(true);
    setTimeout(() => setShowConfetti(false), 4000);

    toast.success('🎉 Daily Health Loop complete!', {
      description: generatedInsight || 'You showed up for yourself today!',
      duration: 5000
    });
  };

  // Checklist items
  const checklistItems = [
    { 
      id: 'water', 
      label: 'Drink water', 
      icon: Droplet, 
      color: 'bg-blue-50 border-blue-200 text-blue-700',
      activeColor: 'bg-blue-100 border-blue-400'
    },
    { 
      id: 'movement', 
      label: 'Move body', 
      icon: Activity, 
      color: 'bg-green-50 border-green-200 text-green-700',
      activeColor: 'bg-green-100 border-green-400'
    },
    { 
      id: 'stress', 
      label: 'Manage stress', 
      icon: Brain, 
      color: 'bg-purple-50 border-purple-200 text-purple-700',
      activeColor: 'bg-purple-100 border-purple-400'
    },
    { 
      id: 'mood', 
      label: 'Check mood', 
      icon: Smile, 
      color: 'bg-pink-50 border-pink-200 text-pink-700',
      activeColor: 'bg-pink-100 border-pink-400'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your daily loop...</p>
        </div>
      </div>
    );
  }

  const progressPercentage = (Object.values(checklist).filter(Boolean).length / 4) * 100;
  const displayedBadges = (badges.earned_badges || []).filter(b => BADGES[b]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
      
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3">
              <Heart className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Daily Health Loop
          </h1>
          <p className="text-gray-600">{encouragementMsg}</p>
        </div>

        {/* Progress Bar */}
        <Card className="border-2 border-purple-200">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Today's Progress</span>
                <span className="text-sm font-semibold text-purple-600">{Math.round(progressPercentage)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personalized Insight */}
        {insight && (
          <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardContent className="pt-5">
              <div className="flex items-start gap-3">
                <Sparkles className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Your Personal Insight</p>
                  <p className="text-sm text-blue-800">{insight}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Checklist */}
        <Card className="border-2 border-gray-200">
          <CardHeader>
            <CardTitle className="text-lg">Today's Habits</CardTitle>
            <p className="text-sm text-gray-600">Simple actions for better health</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {checklistItems.map((item) => {
              const Icon = item.icon;
              const isChecked = checklist[item.id];
              
              return (
                <button
                  key={item.id}
                  onClick={() => handleToggle(item.id)}
                  className={`w-full p-4 rounded-lg border-2 transition-all ${
                    isChecked ? item.activeColor : item.color
                  } hover:shadow-md`}
                >
                  <div className="flex items-center gap-3">
                    {isChecked ? (
                      <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <Circle className="h-6 w-6 flex-shrink-0" />
                    )}
                    <Icon className="h-5 w-5 flex-shrink-0" />
                    <span className="font-medium flex-1 text-left">{item.label}</span>
                  </div>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Streaks */}
        <Card className="border-2 border-orange-200 bg-gradient-to-r from-orange-50 to-amber-50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-600" />
              Your Streaks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">{streaks.daily_streak}</div>
                <div className="text-xs text-gray-600 mt-1">Daily</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{streaks.hydration_streak}</div>
                <div className="text-xs text-gray-600 mt-1">Hydration</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{streaks.movement_streak}</div>
                <div className="text-xs text-gray-600 mt-1">Movement</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Badges */}
        {displayedBadges.length > 0 && (
          <Card className="border-2 border-yellow-200 bg-gradient-to-r from-yellow-50 to-amber-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-yellow-600" />
                Your Badges
              </CardTitle>
              <p className="text-sm text-gray-600">Total Points: {badges.total_points}</p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {displayedBadges.map((badgeKey) => {
                  const badge = BADGES[badgeKey];
                  const BadgeIcon = badge.icon;
                  return (
                    <div 
                      key={badgeKey}
                      className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border-2 border-yellow-300"
                    >
                      <BadgeIcon className={`h-5 w-5 ${badge.color}`} />
                      <span className="text-sm font-medium text-gray-800">{badge.name}</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            onClick={() => navigate('/progress')}
            variant="outline"
            className="flex-1 border-2 border-purple-300 hover:bg-purple-50"
          >
            View Progress
          </Button>
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="flex-1 border-2 border-gray-300 hover:bg-gray-50"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DailyHealthLoop;
